import React, { useEffect, useState } from "react";
import { Phone, X } from "lucide-react";
import { PHONE, PHONE_TEL } from "../data/content";

const STORAGE_KEY = "te-blog-cta-dismissed";

export default function FloatingBlogCta({ slug }) {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Reset on slug change so each new article gets a fresh impression.
    setVisible(false);
    setEntered(false);
    setDismissed(false);

    const alreadyDismissed = (() => {
      try {
        return sessionStorage.getItem(STORAGE_KEY) === slug;
      } catch {
        return false;
      }
    })();
    if (alreadyDismissed) {
      setDismissed(true);
      return;
    }

    const onScroll = () => {
      const article = document.querySelector("article");
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const scrolled = -rect.top;
      const total = Math.max(1, rect.height - window.innerHeight);
      const ratio = scrolled / total;
      if (ratio > 0.6) {
        setVisible(true);
        // tiny delay for slide-in transition
        requestAnimationFrame(() => setEntered(true));
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [slug]);

  const dismiss = () => {
    setEntered(false);
    setTimeout(() => setVisible(false), 250);
    setDismissed(true);
    try { sessionStorage.setItem(STORAGE_KEY, slug); } catch { /* noop */ }
  };

  if (dismissed || !visible) return null;

  return (
    <div
      data-testid="floating-blog-cta"
      className={`fixed z-40 left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm transition-all duration-300 ${
        entered ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      }`}
    >
      <div
        className="te-card p-4 sm:p-5 shadow-2xl border-white/10"
        style={{ background: "rgba(10,10,10,0.96)", backdropFilter: "blur(8px)" }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="floating-cta-dismiss"
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-white transition"
        >
          <X size={14} />
        </button>

        <div className="te-overline text-[10px] mb-1.5">/ Stuck on something?</div>
        <div className="text-base sm:text-lg font-bold tracking-tight mb-1 leading-snug pr-6">
          Get an Adelaide tech to your door.
        </div>
        <div className="text-xs text-zinc-400 mb-3 leading-relaxed">
          Zero callout fee. Free phone triage first.
        </div>
        <a
          href={PHONE_TEL}
          data-testid="floating-cta-call"
          className="te-btn te-btn-primary w-full justify-center text-sm"
        >
          <Phone size={14} /> {PHONE}
        </a>
      </div>
    </div>
  );
}
